import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { FileText } from 'lucide-react';

export const Audit = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAuditLogs();
  }, []);

  const loadAuditLogs = async () => {
    try {
      const response = await api.get('/audit?limit=100');
      setLogs(response.data);
    } catch (error) {
      toast.error('Error al cargar logs de auditoría');
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (accion) => {
    if (accion.includes('Crear')) return 'bg-green-100 text-green-800';
    if (accion.includes('Actualizar')) return 'bg-blue-100 text-blue-800';
    if (accion.includes('Eliminar')) return 'bg-red-100 text-red-800';
    if (accion.includes('Ejecutar')) return 'bg-purple-100 text-purple-800';
    return 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">
          Auditoría del Sistema
        </h1>
        <p className="text-slate-600 mt-1">
          Registro completo de acciones y cambios
        </p>
      </div>

      {/* Audit Log Timeline */}
      <Card className="border border-border">
        <div className="divide-y divide-border">
          {logs.length === 0 ? (
            <div className="p-8 text-center">
              <FileText size={48} className="mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500">No hay registros de auditoría</p>
            </div>
          ) : (
            logs.map((log) => (
              <div
                key={log.id}
                data-testid={`audit-log-${log.id}`}
                className="p-4 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start space-x-4">
                  {/* Timeline dot */}
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Badge className={getActionColor(log.accion)}>
                          {log.accion}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {log.entidad}
                        </Badge>
                      </div>
                      <span className="text-xs text-slate-500 font-mono">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-900 mb-1">
                      <span className="font-medium">{log.usuario_nombre}</span>
                      <span className="text-slate-600 ml-1">({log.usuario_id})</span>
                    </p>
                    <p className="text-xs text-slate-500 font-mono">
                      Entidad ID: {log.entidad_id}
                    </p>
                    {log.detalles && Object.keys(log.detalles).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-700">
                          Ver detalles
                        </summary>
                        <pre className="mt-2 text-xs bg-slate-100 p-2 rounded overflow-x-auto">
                          {JSON.stringify(log.detalles, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      {logs.length > 0 && (
        <div className="mt-4 text-sm text-slate-600">
          Mostrando {logs.length} registro(s) de auditoría
        </div>
      )}
    </div>
  );
};

export default Audit;
